import { useMemo } from 'react';
import { RoiType } from '@weave/schema-gen-ts/dist/schemas/analytics-reporting/analytics_gateway/analytics_gateway.pb';
import { useTranslation } from '@frontend/i18n';
import { useScopedAppFlagStore } from '@frontend/scope';
import { formatters } from '@frontend/shared';
import { featureFlags } from '../../../../feature-flags';
import { findExistingTimePeriodKey } from '../../../../hooks';
import { useFetchARROIOOverview } from '../../appointment-reminders/hooks';
import { RoiSummary, TabCardProps } from '../../components';
import { useNSRRoiShallowStore } from '../hooks';
import { nsrTimePeriods } from './filters';

export const NSRSummary = () => {
  const { t } = useTranslation('analytics');
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const isDemoAccount = getFeatureFlagValue(featureFlags.noShowRateRoiDemoData);
  const { filters } = useNSRRoiShallowStore('filters');
  const timePeriod =
    filters?.startDate && filters?.endDate
      ? findExistingTimePeriodKey<typeof nsrTimePeriods>(filters.startDate, filters.endDate, nsrTimePeriods) ??
        undefined
      : undefined;

  const { aggregated, isLoading } = useFetchARROIOOverview({
    fetchNoShowRate: false,
    filters,
    roiType: RoiType.WEAVE,
  });

  const summaryCardsData: TabCardProps[] = useMemo(() => {
    return [
      {
        title: t('No-Show Rate'),
        data: [
          {
            label: t('{{count}}/{{total}} Completed', {
              count: aggregated.appointmentsCompleted.totalAppointmentsCompleted,
              total: aggregated.totalAppointments,
            }),
            value: formatters.appendPercent(aggregated.noShowRatePercentage),
            infoHoverText: t(
              '{{count}} out of {{total}} appointments scheduled {{timePeriod}} were completed. The remaining {{remaining}} were no-shows, resulting in a {{noShowRate}} no-show rate.',
              {
                count: aggregated.appointmentsCompleted.totalAppointmentsCompleted,
                total: aggregated.totalAppointments,
                remaining: aggregated.totalAppointments - aggregated.appointmentsCompleted.totalAppointmentsCompleted,
                noShowRate: formatters.appendPercent(aggregated.noShowRatePercentage),
                timePeriod: timePeriod ? nsrTimePeriods[timePeriod]?.label?.toLocaleLowerCase() : '',
              }
            ),
          },
        ],
      },
      {
        title: t('Appointments Confirmed'),
        data: [
          {
            label: t('{{count}} Patients', { count: aggregated.appointmentsConfirmed.patientsCount }),
            value: formatters.value(aggregated.appointmentsConfirmed.appointmentsCount),
            infoHoverText: t('A total of {{appointments}} appointments were confirmed by {{patients}} patients', {
              appointments: aggregated.appointmentsConfirmed.appointmentsCount,
              patients: aggregated.appointmentsConfirmed.patientsCount,
            }),
          },
        ],
      },
      {
        title: t('Appointments Completed'),
        data: [
          {
            label: t('{{count}} Patients', { count: aggregated.appointmentsCompleted.appointmentsCompletedPatients }),
            value: formatters.value(aggregated.appointmentsCompleted.totalAppointmentsCompleted),
            infoHoverText: t('A total of {{appointments}} appointments were completed by {{patients}} patients', {
              appointments: aggregated.appointmentsCompleted.totalAppointmentsCompleted,
              patients: aggregated.appointmentsCompleted.appointmentsCompletedPatients,
            }),
          },
        ],
      },
    ];
  }, [aggregated, filters?.startDate, filters?.endDate]);

  return (
    <RoiSummary
      isDemoAccount={isDemoAccount}
      isLoading={isLoading}
      subAlignment='right'
      summaryCardsData={summaryCardsData}
    />
  );
};
